import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { Tooltip } from "@/components/common/Tooltip";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { FilterChip } from "@/components/design/FilterChip";
import { PowerTile } from "@/components/design/PowerTile";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import {
  powerJourneyScale,
  usePowerQuizProgress,
} from "@/components/grow/self-report-progress";
import { PowersDataDocument } from "@/graphql/generated/operations";
import { POWERS_ORIGINS } from "@/lib/powers-origins";
import { ELEMENT_CODES } from "@/lib/elements";
import { combineLevels, type ScoreSource } from "@/lib/score-source";
import { POWER_GROUPS, POWER_LIBRARY, TAG_DESCRIPTION_IDS, TAG_LABEL_IDS } from "@/lib/power-library";
import type { PowerTag } from "@/lib/power-library";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";

// Grow · Power Library (workshop snapshot screens/PowerLibrary.jsx).
// All 30 powers grouped periodic-table-style; searchable by name/abbr.
// Scores come from the user's REAL data (PowersData) - switchable between
// Selbstbewertung (selfAssessmentResults) and Smart Analyse
// (smartAnalysisResults); the static `level` mocks in power-library.ts are
// deliberately NOT used here.

export function PowerLibraryPage() {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  // Which evaluation the tiles display - applies to ALL tiles at once.
  // Selbstbewertung first and by default: it is the score the user produced themselves, and the
  // one the two percentages in the progress card measure the completeness of. Smart Analyse is
  // the second opinion, so it reads second.
  const [scoreSource, setScoreSource] = useState<ScoreSource>("self");
  // Show only powers whose self-assessment is not yet complete, so the user
  // sees at a glance what still needs rating. Pre-activated via ?unrated=1
  // (e.g. the home dashboard's "Unbewertete anzeigen" quick jump).
  const [searchParams] = useSearchParams();
  const [onlyUnrated, setOnlyUnrated] = useState(searchParams.get("unrated") === "1");
  // cache-and-network: a quiz part can give a power its first value, and the
  // tile artwork is gated on having one. Cache-first would hold the old state
  // until a reload.
  const { data } = useQuery(PowersDataDocument, { fetchPolicy: "cache-and-network" });
  // PowersData carries growRanking but not the quiz's per-part flags. Same rule as Home's Grow
  // tile, so the same hook - the two surfaces must not disagree about this number.
  const quiz = usePowerQuizProgress();

  // Both result sets are keyed by CreativityElement; translate to the
  // 3-letter power code so the library tiles can look them up.
  const sa = data?.selfAssessmentResults;
  // Scale for the Power Journey figure. It reads in the tooltip now rather than under the
  // number, but 23 % still means nothing without the 615 it is out of.
  const journey = powerJourneyScale(sa);
  const selfByAbbr = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const r of sa ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code) m.set(code, r.result);
    }
    return m;
  }, [sa]);
  const sm = data?.smartAnalysisResults;
  const smartByAbbr = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const r of sm ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code) m.set(code, r.score);
    }
    return m;
  }, [sm]);
  // Combined lens: see combineLevels - the same rule the profile's Powers box applies.
  const combinedByAbbr = useMemo(() => combineLevels(selfByAbbr, smartByAbbr), [selfByAbbr, smartByAbbr]);

  const levelByAbbr =
    scoreSource === "self" ? selfByAbbr : scoreSource === "smart" ? smartByAbbr : combinedByAbbr;

  // Badge (medal) rule mirrors discovery: shown when the self-assessment for
  // that power is fully answered (answeredQuestionCount === questionCount).
  // Independent of the score-source toggle.
  const completedByAbbr = useMemo(() => {
    const s = new Set<string>();
    for (const r of sa ?? []) {
      const code = ELEMENT_CODES[String(r.element)];
      if (code && r.questionCount > 0 && r.answeredQuestionCount === r.questionCount) s.add(code);
    }
    return s;
  }, [sa]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return POWER_LIBRARY.filter((p) => {
      if (onlyUnrated && completedByAbbr.has(p.abbr)) return false;
      if (!q) return true;
      return (
        intl.formatMessage({ id: p.nameId }).toLowerCase().includes(q) ||
        p.abbr.toLowerCase().includes(q)
      );
    });
  }, [query, onlyUnrated, completedByAbbr, intl]);

  // Global grow score: share of all self-assessment questions answered across
  // every element (genesis GrowRankingController), paired with the earned rank.
  const ranking = data?.growRanking;

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption). NOTE @adr SPARK-0018-bff-proxies-agent-service-as-second-upstream@2: the assistant is integrated with
  // SELF-ASSESSMENT scores ONLY — never surface the smart/combined values here (that would defeat
  // the powerstats+GrowthAdvisor evaluation), regardless of which source the user is viewing.
  const selfScoreLine = useMemo(() => {
    if (!data) return null;
    return POWER_LIBRARY.map((p) => {
      const v = selfByAbbr.get(p.abbr);
      return `${intl.formatMessage({ id: p.nameId })} ${typeof v === "number" ? v : "unrated"}`;
    }).join("; ");
  }, [data, selfByAbbr, intl]);
  useAssistantContext(
    data
      ? [
          {
            description: "Power Map view state",
            value:
              `overall grow score ${ranking?.score ?? "n/a"}%; ` +
              `${completedByAbbr.size}/${POWER_LIBRARY.length} powers fully self-assessed` +
              (onlyUnrated ? "; filtered to unrated only" : "") +
              (query ? `; search "${query}"` : ""),
          },
          {
            description: "the user's self-assessment power scores (0–100 or unrated; the only power scores available to you)",
            value: selfScoreLine ?? "",
          },
        ]
      : null,
  );

  // Guided tour (/tour): the Power Map's controls and the tile grid, self-assessment framing
  // only (@adr SPARK-0018-bff-proxies-agent-service-as-second-upstream@2 — never mention smart/combined values).
  useAssistantTour(
    data
      ? {
          label: intl.formatMessage({
            id: "grow.powerLibrary.tour.label",
            defaultMessage: "Grow Map",
          }),
          summary: intl.formatMessage(
            {
              id: "grow.powerLibrary.tour.summary",
              defaultMessage:
                "This is the Power Map: all {total} powers, periodic-table style. You have fully self-assessed {completed} of {total} so far.",
            },
            { completed: completedByAbbr.size, total: POWER_LIBRARY.length },
          ),
          stops: [
            {
              anchor: "grow.map.scoreSource",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.scoreSource.title",
                defaultMessage: "Score source",
              }),
              description: intl.formatMessage({
                id: "grow.powerLibrary.tour.scoreSource.desc",
                defaultMessage:
                  "Switch which evaluation the numbers on all tiles show. Pick Self-assessment to see the scores from your own ratings.",
              }),
            },
            {
              anchor: "grow.map.filters",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.filters.title",
                defaultMessage: "Search & filter",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerLibrary.tour.filters.desc",
                  defaultMessage:
                    "Search powers by name or abbreviation, or tap Unrated to see only the {unrated} powers whose statements are still open.",
                },
                { unrated: POWER_LIBRARY.length - completedByAbbr.size },
              ),
            },
            {
              anchor: "grow.map.grid",
              title: intl.formatMessage({
                id: "grow.powerLibrary.tour.grid.title",
                defaultMessage: "The power tiles",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerLibrary.tour.grid.desc",
                  defaultMessage:
                    "All {total} powers grouped into {clusters} clusters. Click a tile to open that power. A medal and its artwork appear once all its statements are rated; {completed} earned so far.",
                },
                {
                  total: POWER_LIBRARY.length,
                  clusters: POWER_GROUPS.length,
                  completed: completedByAbbr.size,
                },
              ),
            },
          ],
        }
      : null,
  );

  return (
    <div
      className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="PowersData, PublishedQuestionnaireGroupParts"
      data-assistant-anchor="grow.powerMap"
    >
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <BackButton />
      </div>

      {/* Masthead, then an operator band (bd-requirements#10 header pass).
          Split by who each element is ABOUT, not by what it looks like. The top band is about
          you: identity on the left, your two completeness figures on the right, no box - they
          occupy the empty middle the old right-hand card left behind instead of hugging the edge.
          One hairline, then the band of everything you can operate: the lens that reinterprets
          the numbers, the search and chips that narrow the set, and the assistant hand-off at the
          far end. The hand-off is the one element here that neither names, reinterprets, narrows
          nor reports, so it has no group to join; the row's opposite edge is far enough from the
          chips that proximity stops implying kinship.
          Known trade, accepted: the figures are one band away from the lens whose value they
          qualify (genesis merges exactly these two instruments into it, item-weighted, in
          SelfScoreCalculator.merged). Putting them inside the control was the alternative. */}
      <div>
        <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
          <div className="min-w-0">
            {/* .smycles-page-title, like every h1 under main: the breakpoint (24, ab @sm 36)
                lives in the class, so this call site no longer restates it
                (@adr SPARK-0046-one-type-ladder-no-raw-font-sizes@1). It used to be a flat
                text-5xl, 48px at every width including the narrowest. */}
            <h1 className="smycles-page-title">
              <FormattedMessage
                id="grow.powerLibrary.heading"
                defaultMessage="{count} Powers"
                values={{ count: POWER_LIBRARY.length }}
              />
            </h1>
            <p className="mt-3 max-w-xl text-smycles-body text-ink-muted">
              <FormattedMessage
                id="grow.powerLibrary.subheading"
                defaultMessage="The complete vocabulary: {clusters} clusters, {powers} powers."
                values={{ clusters: POWER_GROUPS.length, powers: POWER_LIBRARY.length }}
              />
            </p>
          </div>

          {/* Right-aligned and boxless: the numbers are the figure, so a panel around them would
              only add an edge. text-smycles-display-m (30), not the 22px this started at: they are
              the right-hand pole of the masthead and the only thing out here, and scale is
              relative - at 22px, alone at the page's edge and 1000px from the page title, they
              read as a footnote. Since the page title moved onto .smycles-page-title (24, ab @sm
              36) these 30px figures are the larger element below @sm - flagged, not yet decided. */}
          <div className="flex shrink-0 gap-8 pb-1.5">
            <Tooltip
              className="cursor-help flex-col items-end"
              // The x/n scale moved in here when it left the figure: it is what makes 66 %
              // against 23 % legible - 6 parts is not 615 statements - but it is reference,
              // not headline, so it sits behind the same hover as the sentence.
              content={
                <>
                  <span className="block">
                    {intl.formatMessage(
                      {
                        id: "grow.powerLibrary.powerQuizTooltip",
                        defaultMessage:
                          "A few questions give you a real overview of all {count} powers.",
                      },
                      // Parameterised like this page's own subheading rather than a literal 30,
                      // so the number cannot go stale behind the taxonomy.
                      { count: POWER_LIBRARY.length },
                    )}
                  </span>
                  {quiz.percent !== null ? (
                    <span className="mt-1 block tabular-nums text-ink-muted">
                      {intl.formatMessage(
                        {
                          id: "power.instrument.parts",
                          defaultMessage: "{answered} / {total} parts",
                        },
                        { answered: quiz.answered, total: quiz.total },
                      )}
                    </span>
                  ) : null}
                </>
              }
            >
              <div className="smycles-field-label">
                <FormattedMessage id="power.instrument.quiz" defaultMessage="Power Quiz" />
              </div>
              <div className="mt-1 whitespace-nowrap font-serif text-smycles-display-m leading-none tabular-nums">
                {quiz.loading ? <span className="text-ink-muted">…</span> : null}
                {!quiz.loading && quiz.percent === null ? "–" : null}
                {!quiz.loading && quiz.percent !== null ? <>{quiz.percent}&thinsp;%</> : null}
              </div>
              {/* Only the percentage - the x/n scale that used to sit here restated what the
                  figure already says. What stays is the absence: nothing while loading, because
                  percent is null both before the fetch lands and when no group is published, so
                  branching on it alone claims "not available yet" about something the app has not
                  learned yet. smycles-field-meta, not a hand-picked 11px: the field scale reserves
                  12px for "hints, dates, secondary info". */}
              {!quiz.loading && quiz.percent === null ? (
                <div className="mt-1 whitespace-nowrap smycles-field-meta text-ink-muted">
                  <FormattedMessage
                    id="grow.powerLibrary.quizUnavailable"
                    defaultMessage="not available yet"
                  />
                </div>
              ) : null}
            </Tooltip>
            {ranking ? (
              <Tooltip
                className="cursor-help flex-col items-end"
                content={
                  <>
                    <span className="block">
                      {intl.formatMessage({ id: "grow.powerLibrary.journeyTooltip" })}
                    </span>
                    <span className="mt-1 block tabular-nums text-ink-muted">
                      {intl.formatMessage(
                        {
                          id: "power.instrument.inDepthStatements",
                          defaultMessage: "{answered} / {total} in-depth statements",
                        },
                        { answered: journey.answered, total: journey.total },
                      )}
                    </span>
                  </>
                }
              >
                <div className="smycles-field-label">
                  <FormattedMessage
                    id="power.instrument.journey"
                    defaultMessage="Power Journey"
                  />
                </div>
                <div className="mt-1 whitespace-nowrap font-serif text-smycles-display-m leading-none tabular-nums">
                  {ranking.score}&thinsp;%
                </div>
              </Tooltip>
            ) : null}
          </div>
        </div>

        {/* No rule between the masthead and this band. The two are already told apart by form -
            display type and small figures above, ring-bordered pills and chips below - so a line
            would only restate what the shapes say. mt-12 on the 8pt grid, deliberately more than
            the ~41px the rule and its padding used to occupy: with no line to stop the eye, the
            space has to carry the separation on its own, so it gets more of it rather than the
            same. */}
        <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3">
          {/* gap-x-8 between the lens and the filters, where the inner groups run at gap-2/gap-3:
              reinterpreting and narrowing are different jobs, and the space says so. No rule
              between them - a divider inside a single row is an admission that the spacing failed,
              same reason the progress card's cross of hairlines came out. */}
          {/* Lens — reinterprets every tile's number. Its own anchor, so the tour step still
              points at the control and not at the whole band. */}
          <div
            className="flex flex-wrap items-center gap-2"
            data-assistant-anchor="grow.map.scoreSource"
          >
            <span className="smycles-field-label text-ink">
              <FormattedMessage id="grow.powerLibrary.powerScore" defaultMessage="Power Score" />
            </span>
            <SegmentedControl<ScoreSource>
              value={scoreSource}
              onChange={setScoreSource}
              options={[
                {
                  value: "self",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceSelf",
                    defaultMessage: "Self-assessment",
                  }),
                },
                {
                  value: "smart",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceSmart",
                    defaultMessage: "Smart Analysis",
                  }),
                },
                {
                  value: "combined",
                  label: intl.formatMessage({
                    id: "grow.powerLibrary.sourceCombined",
                    defaultMessage: "Combined",
                  }),
                },
              ]}
            />
          </div>

          {/* Filters — both narrow the visible set. */}
          <div
            className="flex flex-wrap items-center gap-3"
            data-assistant-anchor="grow.map.filters"
          >
            {/* w-[13rem], not w-full+max-w: inside a flex-wrap row `width:100%` lets the field
                claim a whole line and pushes the chips onto the next one. A fixed width keeps
                search and chips on one line, which is what they are - one group. */}
            <div className="flex w-[13rem] shrink-0 items-center gap-2 rounded-full bg-transparent px-4 py-1.5 ring-1 ring-ink/15 transition focus-within:ring-ink-ui">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "grow.powerLibrary.searchPlaceholder",
                  defaultMessage: "Search by name or abbreviation",
                })}
                className="flex-1 bg-transparent text-smycles-meta text-ink placeholder:text-ink-muted focus:outline-none"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={intl.formatMessage({
                    id: "grow.powerLibrary.clearSearch",
                    defaultMessage: "Clear search",
                  })}
                  className="shrink-0 text-smycles-body text-ink-muted hover:text-ink"
                >
                  ✕
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip active={!onlyUnrated} onClick={() => setOnlyUnrated(false)}>
                <FormattedMessage id="grow.powerLibrary.all" defaultMessage="All" />
              </FilterChip>
              <FilterChip active={onlyUnrated} onClick={() => setOnlyUnrated(true)}>
                <FormattedMessage id="grow.powerLibrary.onlyUnrated" defaultMessage="Unrated" />
              </FilterChip>
            </div>
          </div>

          {/* Third group in this band, at the same gap-x-8 that separates the lens from the
              filters. The band is the things you DO - reinterpret, narrow, hand off - where the
              masthead above is the things you READ. Not pushed to the page edge by
              justify-between: one group's distance is enough to say it is not a third filter.
              Bordered like the ask buttons on the Power detail page. */}
        <AskAssistantButton
          variant="secondary"
          // Randomize among 10 equivalent phrasings (grow.powerLibrary.askIntro.0..9) for variety.
          prompt={() =>
            intl.formatMessage({
              id: `grow.powerLibrary.askIntro.${Math.floor(Math.random() * 10)}`,
            })
          }
          label={<FormattedMessage id="grow.powerLibrary.askButton" />}
        />

        </div>
      </div>

      {/* Periodic-table layout: each of the six genesis clusters sits in its
          own framed block (3 per row on desktop), in the stable POWER_GROUPS
          order. Search acts inside the blocks; empty blocks disappear. */}
      <div
        id="grow-map"
        className="mt-6 grid grid-cols-1 items-start gap-3 @sm:grid-cols-2 @lg:grid-cols-3"
        data-assistant-anchor="grow.map.grid"
      >
        {POWER_GROUPS.map((t) => (
          <ClusterBlock
            key={t}
            tag={t}
            entries={filtered.filter((p) => p.tag === t)}
            levelByAbbr={levelByAbbr}
            badgeByAbbr={completedByAbbr}
          />
        ))}
      </div>

      <footer className="mt-8 max-w-[75ch] text-smycles-meta leading-relaxed text-ink-muted">
        <p>
          <FormattedMessage
            id="grow.about.attribution"
            defaultMessage="Smycles’ <concept>Powers concept</concept> draws on {author}’s work."
            values={{
              author: intl.formatMessage(POWERS_ORIGINS.author),
              concept: (chunks) => (
                <Link
                  id="grow-map-about-powers"
                  to="/grow/about-powers"
                  className="underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        </p>
      </footer>

      {filtered.length === 0 ? (
        <div className="mt-12 text-center text-ink-muted">
          <FormattedMessage
            id="grow.powerLibrary.noResults"
            defaultMessage="No power found for “{query}”."
            values={{ query }}
          />
        </div>
      ) : null}
    </div>
  );
}

// Gap-filler colSpan for the tile grid's trailing row, looked up rather than
// interpolated (`col-span-${n}`) because Tailwind's JIT only picks up literal
// class strings from source. Two parallel tables because `grid-cols-3
// @sm:grid-cols-4` is a container query, not a viewport breakpoint: the
// active column count depends on the ClusterBlock's own rendered width, so
// both possible gaps must be encoded and let CSS pick the one that applies.
const GAP_SPAN_3: readonly string[] = ["", "col-span-1", "col-span-2"];
const GAP_SPAN_SM_4: readonly string[] = ["", "@sm:col-span-1", "@sm:col-span-2", "@sm:col-span-3"];

// Spike (#248): with the stable 5-powers-per-cluster count this always finds
// a gap at both container widths (5 % 3 = 2, 5 % 4 = 1). Under an active
// search filter a cluster can show fewer tiles; if that happens to fill a row
// exactly, the description silently doesn't render at that width -- an
// acceptable trade for a spike, not a regression (there's no description at
// all otherwise).
function clusterGapSpanClassName(count: number): string {
  const gap3 = (3 - (count % 3)) % 3;
  const gap4 = (4 - (count % 4)) % 4;
  return [GAP_SPAN_3[gap3], GAP_SPAN_SM_4[gap4]].filter(Boolean).join(" ");
}

// One framed cluster block of the "periodic table": header with the cluster
// letter (element-symbol style) + name + count, a faint watermark letter, and
// the power tiles inside.
function ClusterBlock({
  tag,
  entries,
  levelByAbbr,
  badgeByAbbr,
}: {
  tag: PowerTag;
  entries: readonly (typeof POWER_LIBRARY)[number][];
  levelByAbbr: ReadonlyMap<string, number | null | undefined>;
  badgeByAbbr: ReadonlySet<string>;
}) {
  const navigate = useNavigate();
  const intl = useIntl();
  if (entries.length === 0) return null;
  return (
    // group/cluster is a NAMED group: PowerTile's own hover-reveal (the tile
    // artwork) uses an unnamed `group`/`group-hover:` pair internally, and an
    // unnamed group here would answer to that same generic selector -- every
    // tile's artwork would pop in together on cluster hover instead of only
    // on that tile's own hover.
    <section className="group/cluster box-flat relative overflow-hidden bg-surface p-3">
      <div className="mb-2 flex items-baseline gap-2">
        <span className="smycles-field-meta font-medium">{intl.formatMessage({ id: TAG_LABEL_IDS[tag] })}</span>
        <span className="smycles-field-label text-ink-muted">{entries.length}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 @sm:grid-cols-4">
        {entries.map((p) => {
          const level = levelByAbbr.get(p.abbr);
          const isMeasured = typeof level === "number";
          // Artwork unlocks as soon as the power has a value from any source -
          // a questionnaire-only score counts. The medal stays stricter: it is
          // the reward for completing the per-element self-assessment.
          const rated = badgeByAbbr.has(p.abbr);
          return (
            <PowerTile
              key={p.abbr}
              id={`grow-tile-${p.abbr}`}
              tag={p.tag}
              abbr={p.abbr}
              name={intl.formatMessage({ id: p.nameId })}
              // Uniform card style regardless of source/measured - only the
              // number appears/changes when toggling the score source.
              active={false}
              score={isMeasured ? level : null}
              // Medal when the self-assessment for this power is complete.
              badge={rated}
              imageSrc={isMeasured || rated ? `/powers/${p.abbr.toLowerCase()}.jpg` : null}
              onSelect={() => navigate(`/grow/power/${p.abbr}`)}
              selectTitle={intl.formatMessage(
                { id: "grow.powerLibrary.openPower", defaultMessage: "Open {name}" },
                { name: intl.formatMessage({ id: p.nameId }) },
              )}
            />
          );
        })}
        {/* Spike (#248): cluster description fills the tile grid's trailing
            gap and fades in on hover of the cluster -- the header or any
            tile, since :hover bubbles up to .group\/cluster regardless of
            depth. Named group (see the section's className) so this doesn't
            also fire PowerTile's own unnamed group-hover (its artwork reveal).
            Not the scale+blur curve of that artwork reveal (#255): blur-md
            is a 12px radius on 12px text, wider than the glyph strokes, so
            the first frames were an unreadable smear, and animating scale
            promotes the paragraph to its own layer, which bitmap-scales the
            glyphs on top of that. Tailwind compiles group-hover inside
            @media (hover: hover), so on a touch-only iPad the reveal could
            not fire at all; pointer-coarse pins it visible there instead.
            group-focus-within is the keyboard half of the same idiom -- a
            sighted keyboard user tabs to the tiles and never hovers, so the
            copy was as unreachable for them as on the iPad (DocItemRow and
            DocPreviewCard pair the two variants the same way). No
            aria-hidden: wherever this is permanently visible it is visible
            text, and hiding visible copy from assistive tech is a defect. */}
        <p
          className={`flex items-center smycles-field-meta text-ink-muted opacity-0 transition-opacity duration-[var(--duration-fast)] ease-out group-hover/cluster:opacity-100 group-focus-within/cluster:opacity-100 pointer-coarse:opacity-100 ${clusterGapSpanClassName(entries.length)}`}
        >
          {/* Bold cluster name up front (instead of a repeated "core value of
              this cluster" preamble in the copy) -- reuses TAG_LABEL_IDS
              rather than duplicating the name inside the description string.
              Wrapped in one <span> so name+text stay a single flex item and
              wrap as normal running text; as two direct children of this
              flex <p> they'd sit side by side as separate flex items instead. */}
          <span>
            <strong className="font-semibold">
              {intl.formatMessage({ id: TAG_LABEL_IDS[tag] })}:
            </strong>{" "}
            {intl.formatMessage({ id: TAG_DESCRIPTION_IDS[tag] })}
          </span>
        </p>
      </div>
      {/* Faint element-symbol watermark in the top-right corner. Sized
          (text-smycles-display-s) to fit within the clean header band above the tile grid:
          the band is ~28px tall (p-3 top + smycles-field-meta line + mb-2), so a
          larger text-smycles-display-l mark spilled onto the first tile row, where artwork on
          a rated power swallowed its lower half. At this size the full
          two-letter mark stays on clean surface. pointer-events-none keeps
          clicks passing through. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-2 top-2 z-20 select-none font-serif text-smycles-display-s leading-none text-ink/[0.12]"
      >
        {tag}
      </span>
    </section>
  );
}
